import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { BookOpen, FileText, Video, MessageSquare } from "lucide-react";

export default function Education() {
  const modules = [
    {
      title: "NIL Basics",
      icon: <BookOpen className="h-6 w-6" />,
      topics: [
        {
          title: "What is NIL?",
          content: "Name, Image, and Likeness (NIL) refers to the right of college athletes to profit from their personal brand through endorsements, sponsorships, and other commercial opportunities.",
        },
        {
          title: "NCAA NIL Rules",
          content: "The NCAA allows student-athletes to benefit from their NIL while maintaining amateur status. Athletes must comply with state laws, institutional policies, and NCAA regulations.",
        },
        {
          title: "State-by-State Variations",
          content: "NIL laws vary significantly by state. Some states have more restrictive policies than others regarding disclosure requirements, prohibited deal types, and institutional involvement.",
        },
      ],
    },
    {
      title: "Compliance Requirements",
      icon: <FileText className="h-6 w-6" />,
      topics: [
        {
          title: "Disclosure Requirements",
          content: "Athletes must disclose all NIL activities to their institution within specific timeframes. Most institutions require disclosure before or within 48-72 hours of deal execution.",
        },
        {
          title: "Prohibited Deal Types",
          content: "Pay-for-play deals, recruitment incentives, and deals tied to athletic performance are strictly prohibited. Deals cannot use school logos or marks without permission.",
        },
        {
          title: "Valid Business Purpose",
          content: "All NIL deals must have a valid business purpose and fair market value compensation. Athletes must provide genuine services or promotional value in exchange for compensation.",
        },
      ],
    },
    {
      title: "Title IX & Revenue Sharing",
      icon: <Video className="h-6 w-6" />,
      topics: [
        {
          title: "Title IX Compliance",
          content: "Institutions must ensure gender equity in NIL opportunities and revenue sharing. Distribution of resources and support must be proportional to enrollment and participation rates.",
        },
        {
          title: "CAPS Revenue Sharing",
          content: "The College Athlete Prosperity System (CAPS) allows institutions to share revenue with athletes. Funds must be distributed equitably across sports and genders.",
        },
        {
          title: "Monitoring Gender Equity",
          content: "Athletic departments must regularly assess NIL opportunities, revenue sharing pools, and support services to ensure compliance with Title IX requirements.",
        },
      ],
    },
    {
      title: "Best Practices",
      icon: <MessageSquare className="h-6 w-6" />,
      topics: [
        {
          title: "Deal Evaluation",
          content: "Before accepting a deal, athletes should evaluate the brand alignment, time commitment, compensation fairness, and potential conflicts with institutional sponsors.",
        },
        {
          title: "Tax Considerations",
          content: "NIL income is taxable. Athletes should maintain detailed records, set aside money for quarterly estimated tax payments, and consider working with a tax professional.",
        },
        {
          title: "Brand Building",
          content: "Long-term success requires building an authentic personal brand, maintaining professional social media presence, and engaging genuinely with fans and followers.",
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">NIL Education Center</h1>
            <p className="text-xl text-muted-foreground">
              Comprehensive resources to understand and navigate the NIL landscape
            </p>
          </div>

          <div className="space-y-8">
            {modules.map((module, index) => (
              <Card key={index}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      {module.icon}
                    </div>
                    <div>
                      <CardTitle>{module.title}</CardTitle>
                      <CardDescription>Essential knowledge for compliance</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="w-full">
                    {module.topics.map((topic, topicIndex) => (
                      <AccordionItem key={topicIndex} value={`item-${topicIndex}`}>
                        <AccordionTrigger>{topic.title}</AccordionTrigger>
                        <AccordionContent>
                          <p className="text-muted-foreground leading-relaxed">
                            {topic.content}
                          </p>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="mt-8 bg-primary/5 border-primary/20">
            <CardHeader>
              <CardTitle>Need More Help?</CardTitle>
              <CardDescription>
                Our compliance team is here to support you
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Contact your institutional compliance officer for personalized guidance on NIL matters,
                or reach out to our support team for platform-specific questions.
              </p>
              <div className="flex gap-4">
                <span className="text-sm font-medium">Email:</span>
                <a href="mailto:compliance@yourschool.edu" className="text-sm text-primary hover:underline">
                  compliance@yourschool.edu
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
